// ============================================================================
// brand-accent — the ONE place the tenant accent reaches the DOM
// ----------------------------------------------------------------------------
// SPEC-settings.md §7 (required non-cosmetic change). The accent-family
// injection used to live inline inside useBrand()'s []-dep effect in
// src/config/brand.js, which meant nothing could re-run it after a save: a
// saved brand colour did not retint until a full reload. Extracting it lets
// the Settings Brand card preview live, revert cleanly, and commit on save,
// while useBrand() calls the same function so boot and preview cannot drift.
//
// The mix() weights are VERBATIM from the original injection (brand.js,
// white-label link 2026-07-28): bright = +14% white, hover = +42% white,
// deep = +14% black, pressed = +26% black, edge = +34% black. Changing a
// weight here changes every accent stop app-wide — do not tune casually.
// ============================================================================

/** The platform default brand colour. tokens.css holds the same value. */
export const DEFAULT_ACCENT = '#EB7232';

export const ACCENT_PROPERTIES = [
    '--qm-accent',
    '--qm-accent-bright',
    '--qm-accent-hover',
    '--qm-accent-deep',
    '--qm-accent-pressed',
    '--qm-accent-edge',
    '--qm-accent-contrast',
    // The ladder's text stop. tokens.css derives it; a tenant brand gets a
    // value darkened, where needed, to 4.5:1 on the lightest tint (see
    // accentTextStop), which the relative-oklch formula alone cannot promise.
    '--qm-accent-700',
    // The select-box caret image (tokens.css). A data URI cannot read var().
    '--qm-accent-caret',
] as const;

/** The select-box caret as tokens.css writes it, in the given colour. */
export function accentCaret(hex: string): string {
    const encoded = `%23${String(hex).replace('#', '')}`;
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${encoded}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function mix(hex: string, other: '#fff' | '#000', weight: number): string {
    const c = (x: string, i: number) => parseInt(x.slice(i, i + 2), 16);
    const [r1, g1, b1] = [c(hex, 1), c(hex, 3), c(hex, 5)];
    const [r2, g2, b2] = other === '#fff' ? [255, 255, 255] : [0, 0, 0];
    const m = (a: number, b: number) => Math.round(a * (1 - weight) + b * weight);
    return `#${[m(r1, r2!), m(g1, g2!), m(b1, b2!)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function relativeLuminance(hex: string): number | null {
    const value = String(hex || '').trim();
    if (!HEX_RE.test(value)) return null;
    const chan = (i: number) => {
        const c = parseInt(value.slice(i, i + 2), 16) / 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * chan(1) + 0.7152 * chan(3) + 0.0722 * chan(5);
}

/** WCAG contrast ratio for two six-digit hex colours. */
export function contrastRatio(foreground: string, background: string): number | null {
    const foregroundLuminance = relativeLuminance(foreground);
    const backgroundLuminance = relativeLuminance(background);
    if (foregroundLuminance === null || backgroundLuminance === null) return null;
    const lighter = Math.max(foregroundLuminance, backgroundLuminance);
    const darker = Math.min(foregroundLuminance, backgroundLuminance);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick the higher-contrast monochrome foreground for an accent fill. Pure
 * black and white are intentional: one of them always clears WCAG AA 4.5:1,
 * including mid-luminance colours where brand ink and white can both fail.
 */
export function accentContrastOn(hex: string): { color: '#000000' | '#ffffff'; ratio: number } | null {
    const blackRatio = contrastRatio('#000000', hex);
    const whiteRatio = contrastRatio('#ffffff', hex);
    if (blackRatio === null || whiteRatio === null) return null;
    return blackRatio >= whiteRatio
        ? { color: '#000000', ratio: blackRatio }
        : { color: '#ffffff', ratio: whiteRatio };
}

/** Derive the six historical stops plus an accessible foreground. */
export function deriveAccentStops(hex: string): Record<string, string> {
    const contrast = accentContrastOn(hex);
    return {
        '--qm-accent': hex,
        '--qm-accent-bright': mix(hex, '#fff', 0.14),
        '--qm-accent-hover': mix(hex, '#fff', 0.42),
        '--qm-accent-deep': mix(hex, '#000', 0.14),
        '--qm-accent-pressed': mix(hex, '#000', 0.26),
        '--qm-accent-edge': mix(hex, '#000', 0.34),
        '--qm-accent-contrast': contrast?.color || '#000000',
        '--qm-accent-700': accentTextStop(hex),
        '--qm-accent-caret': accentCaret(hex),
    };
}

/**
 * Apply a tenant accent to :root. No-ops on a malformed hex rather than
 * half-writing the family. The alpha ladder (--qm-accent-a05..a30) needs no
 * work here: tokens.css derives it via color-mix() from --qm-accent.
 */
export function applyBrandAccent(hex: string): void {
    if (typeof document === 'undefined') return;
    const value = String(hex || '').trim();
    if (!HEX_RE.test(value)) return;
    const stops = deriveAccentStops(value);
    const root = document.documentElement.style;
    for (const prop of ACCENT_PROPERTIES) root.setProperty(prop, stops[prop]!);
    setBrowserThemeColor(value);
}

/**
 * Snapshot the CURRENT inline values ('' where unset) so a preview can be
 * reverted exactly — including back to "nothing injected at all".
 */
export function snapshotBrandAccent(): Record<string, string> {
    const snap: Record<string, string> = {};
    if (typeof document === 'undefined') return snap;
    const root = document.documentElement.style;
    for (const prop of ACCENT_PROPERTIES) snap[prop] = root.getPropertyValue(prop) || '';
    snap[THEME_COLOR_SNAPSHOT_KEY] = readBrowserThemeColor();
    return snap;
}

/**
 * Restore a snapshot. MUST removeProperty where the snapshot holds '' rather
 * than writing the old value back, so the color-mix() derivations in
 * tokens.css resume when the tenant colour is the default. An inline value on
 * :root beats the stylesheet, which is why a missed removeProperty leaves the
 * whole app stuck on the previewed colour.
 */
export function restoreBrandAccent(snap: Record<string, string>): void {
    if (typeof document === 'undefined' || !snap) return;
    const root = document.documentElement.style;
    for (const prop of ACCENT_PROPERTIES) {
        const value = snap[prop];
        if (value) root.setProperty(prop, value);
        else root.removeProperty(prop);
    }
    // An empty snapshot ({}, which sign-out and the Settings clear path pass)
    // means "nothing injected", so the title bar goes back to the default too.
    setBrowserThemeColor(snap[THEME_COLOR_SNAPSHOT_KEY] || DEFAULT_ACCENT);
}

/**
 * Contrast of white text on the given hex. Kept for callers that need the
 * white-only measurement; accentContrastOn is the safe foreground contract.
 */
export function whiteContrastOn(hex: string): number | null {
    return contrastRatio('#ffffff', hex);
}

// ============================================================================
// Accent ladder (2026-09-29)
// ----------------------------------------------------------------------------
// The tints and shades of the ONE brand colour. tokens.css writes each stop as
// relative oklch from --qm-accent; this table is the same numbers for the
// places CSS cannot reach: print and PDF documents that do not load
// tokens.css, mermaid (which parses colour strings itself), canvas, and the
// invitation email. `l` is an absolute oklch lightness, `c` scales the brand's
// chroma, `h` shifts the brand's hue in degrees. They were measured from the
// Tailwind orange ladder against #EB7232, so the default brand reproduces the
// historical hexes exactly. src/__tests__/brand-accent-ladder.test.ts pins
// this table to tokens.css.
// ============================================================================

export const ACCENT_LADDER: Readonly<Record<string, { l: number; c: number; h: number }>> = {
    '50': { l: 0.9796, c: 0.0941, h: 28.24 },
    '100': { l: 0.9542, c: 0.2218, h: 29.72 },
    '200': { l: 0.9015, c: 0.4355, h: 25.26 },
    '300': { l: 0.8366, c: 0.6957, h: 20.85 },
    '400': { l: 0.7576, c: 0.9494, h: 10.49 },
    '500': { l: 0.7049, c: 1.1146, h: 2.16 },
    '600': { l: 0.6461, c: 1.16, h: -4.32 },
    '700': { l: 0.5534, c: 1.0379, h: -7.04 },
    '800': { l: 0.4698, c: 0.8539, h: -8.14 },
    '900': { l: 0.4084, c: 0.6952, h: -7.27 },
    '950': { l: 0.2659, c: 0.455, h: -9.18 },
    light: { l: 0.7557, c: 0.8132, h: 4.39 },
};

/** tokens.css --qm-accent-on switches from white to black above this oklch L. */
const ON_ACCENT_LIGHTNESS = 0.72;
/** tokens.css --qm-accent-on-dark never goes darker than this oklch L. */
const ON_DARK_MIN_LIGHTNESS = 0.68;
/** tokens.css default for --qm-accent-contrast (the strict pick is JS-injected). */
const DEFAULT_ACCENT_CONTRAST = '#1a1a1a';
const ALPHA_STOPS = [5, 10, 15, 20, 25, 30] as const;
const THEME_COLOR_SNAPSHOT_KEY = 'meta:theme-color';
const THEME_COLOR_METAS = ['theme-color', 'msapplication-TileColor'];

type Oklch = { l: number; c: number; h: number };

const toLinear = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};
const fromLinear = (v: number) => {
    const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return c * 255;
};

function hexToOklch(hex: string): Oklch {
    const value = String(hex).replace('#', '');
    const r = toLinear(parseInt(value.slice(0, 2), 16));
    const g = toLinear(parseInt(value.slice(2, 4), 16));
    const b = toLinear(parseInt(value.slice(4, 6), 16));
    const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
    const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
    const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
    let h = (Math.atan2(B, A) * 180) / Math.PI;
    if (h < 0) h += 360;
    return { l: L, c: Math.hypot(A, B), h };
}

function oklchToHex({ l, c, h }: Oklch): string {
    const rad = (h * Math.PI) / 180;
    const A = c * Math.cos(rad);
    const B = c * Math.sin(rad);
    const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3;
    const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3;
    const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3;
    const rgb = [
        4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
        -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
        -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
    ];
    const channel = (v: number) => Math.max(0, Math.min(255, Math.round(fromLinear(Math.max(0, Math.min(1, v))))));
    return `#${rgb.map((v) => channel(v).toString(16).padStart(2, '0')).join('')}`;
}

/** color-mix(in oklch, hex P%, white|black), as tokens.css computes the working stops. */
function oklchMix(hex: string, weight: number, toward: 'white' | 'black'): string {
    const base = hexToOklch(hex);
    const target = toward === 'white' ? 1 : 0;
    return oklchToHex({ l: base.l * weight + target * (1 - weight), c: base.c * weight, h: base.h });
}

function validHex(hex: unknown): string | null {
    const value = String(hex || '').trim();
    return HEX_RE.test(value) ? value : null;
}

/**
 * One shade of the brand as a hex: an absolute oklch lightness `l`, the
 * brand's chroma scaled by `c`, its hue shifted by `h` degrees. The same
 * formula CSS evaluates for `oklch(from var(--qm-accent) l calc(c * c) calc(h + h))`,
 * which is how the codemod writes a shade that is not a ladder stop.
 */
export function accentShade(hex: string, p: { l: number; c: number; h: number }): string {
    const brand = hexToOklch((validHex(hex) || DEFAULT_ACCENT).toLowerCase());
    return oklchToHex({ l: p.l, c: brand.c * p.c, h: brand.h + p.h });
}

/** Every --qm-accent-N stop (and -light) as a hex for the given brand. */
export function accentLadder(hex: string): Record<string, string> {
    const ladder: Record<string, string> = {};
    for (const [stop, p] of Object.entries(ACCENT_LADDER)) {
        ladder[`--qm-accent-${stop}`] = accentShade(hex, p);
    }
    return ladder;
}

/**
 * The 700 stop is the ladder's text colour (#c2410c at the default brand,
 * 183 uses). Its absolute lightness clears 4.5:1 on white for most hues, but
 * cyan and green brands land a little under 4.5:1 on the 50 tint that chips
 * sit on. Darken it until it clears, so a tenant's chip text stays readable.
 * The default brand already clears it (4.88:1) and is returned unchanged.
 */
export function accentTextStop(hex: string): string {
    const value = (validHex(hex) || DEFAULT_ACCENT).toLowerCase();
    const brand = hexToOklch(value);
    const p = ACCENT_LADDER['700']!;
    const tint = accentLadder(value)['--qm-accent-50']!;
    let l = p.l;
    let stop = oklchToHex({ l, c: brand.c * p.c, h: brand.h + p.h });
    while ((contrastRatio(stop, tint) ?? 0) < 4.5 && l > 0.3) {
        l -= 0.005;
        stop = oklchToHex({ l, c: brand.c * p.c, h: brand.h + p.h });
    }
    return stop;
}

/**
 * Every accent token the app renders, resolved to concrete colours for the
 * given brand. The default brand resolves the working stops the way tokens.css
 * does (color-mix in oklch); any other brand resolves them the way
 * applyBrandAccent() injects them, so a document matches the screen it came
 * from. Alpha stops are rgba() strings.
 */
export function resolvedAccentTokens(hex: string): Record<string, string> {
    const value = (validHex(hex) || DEFAULT_ACCENT).toLowerCase();
    const isDefault = value === DEFAULT_ACCENT.toLowerCase();
    const brand = hexToOklch(value);
    const stops: Record<string, string> = isDefault
        ? {
            '--qm-accent': value,
            '--qm-accent-bright': oklchMix(value, 0.88, 'white'),
            '--qm-accent-hover': oklchMix(value, 0.55, 'white'),
            '--qm-accent-deep': oklchMix(value, 0.9, 'black'),
            '--qm-accent-pressed': oklchMix(value, 0.8, 'black'),
            '--qm-accent-edge': oklchMix(value, 0.7, 'black'),
            '--qm-accent-contrast': DEFAULT_ACCENT_CONTRAST,
        }
        : { ...deriveAccentStops(value), '--qm-accent': value };
    // Injected stops win over the formula values, as they do on screen.
    const tokens: Record<string, string> = { ...accentLadder(value), ...stops };
    tokens['--qm-accent-on'] = brand.l < ON_ACCENT_LIGHTNESS ? '#ffffff' : '#000000';
    tokens['--qm-accent-on-dark'] = oklchToHex({ l: Math.max(brand.l, ON_DARK_MIN_LIGHTNESS), c: brand.c, h: brand.h });
    const r = parseInt(value.slice(1, 3), 16);
    const g = parseInt(value.slice(3, 5), 16);
    const b = parseInt(value.slice(5, 7), 16);
    for (const pct of ALPHA_STOPS) {
        tokens[`--qm-accent-a${String(pct).padStart(2, '0')}`] = `rgba(${r}, ${g}, ${b}, ${pct / 100})`;
    }
    return tokens;
}

/**
 * The brand colour the app is showing right now: the tenant accent injected
 * on :root, or the platform default. Read this, never a hex literal, when a
 * surface CSS cannot reach needs the brand (print, mermaid, canvas).
 */
export function currentAccentHex(): string {
    if (typeof document !== 'undefined') {
        const injected = validHex(document.documentElement.style.getPropertyValue('--qm-accent'));
        if (injected) return injected.toLowerCase();
    }
    return DEFAULT_ACCENT.toLowerCase();
}

/** One accent token as a concrete colour for the brand on screen right now. */
export function currentAccentToken(token: string): string {
    return resolvedAccentTokens(currentAccentHex())[token] || currentAccentHex();
}

/**
 * The caution family (orange that MEANS a warning), as tokens.css defines it.
 * It never follows the brand. Listed here only so a document that does not
 * load tokens.css can still resolve var(--qm-caution-*);
 * src/__tests__/brand-accent-ladder.test.ts keeps it identical to tokens.css.
 */
export const CAUTION_TOKENS: Readonly<Record<string, string>> = {
    '--qm-caution-50': '#fff7ed',
    '--qm-caution-100': '#ffedd5',
    '--qm-caution-200': '#fed7aa',
    '--qm-caution-300': '#fdba74',
    '--qm-caution-400': '#fb923c',
    '--qm-caution-500': '#f97316',
    '--qm-caution-600': '#ea580c',
    '--qm-caution-700': '#c2410c',
    '--qm-caution-800': '#9a3412',
    '--qm-caution-900': '#7c2d12',
    '--qm-caution-950': '#431407',
    '--qm-caution-ink': '#8a4b1e',
};

/**
 * A `:root{...}` rule with every accent token as a concrete colour (plus the
 * fixed caution family), for a print or PDF document that does not inherit
 * the app's :root. The document's own CSS then uses var(--qm-accent-*)
 * exactly like the app.
 */
export function brandAccentCssText(hex: string = currentAccentHex()): string {
    const tokens = { ...resolvedAccentTokens(hex), ...CAUTION_TOKENS };
    return `:root{${Object.entries(tokens).map(([name, value]) => `${name}:${value};`).join('')}}`;
}

/**
 * brandAccentCssText as a <style> element. Every print, PDF and exported HTML
 * document writes it into its <head>, AFTER any stylesheet copied from the app
 * (those carry tokens.css's default brand), so the business's colour wins.
 */
export function brandAccentStyleTag(hex: string = currentAccentHex()): string {
    return `<style data-qm-brand-accent>${brandAccentCssText(hex)}</style>`;
}

function readBrowserThemeColor(): string {
    if (typeof document === 'undefined') return '';
    return document.querySelector('meta[name="theme-color"]')?.getAttribute('content') || '';
}

/** The browser and installed-app title bar colour follows the brand. */
function setBrowserThemeColor(hex: string): void {
    if (typeof document === 'undefined') return;
    for (const name of THEME_COLOR_METAS) {
        const meta = document.querySelector(`meta[name="${name}"]`);
        if (meta) meta.setAttribute('content', hex);
    }
}

// ----------------------------------------------------------------------------
// The signed-in tenant's brand
// ----------------------------------------------------------------------------

/** Where this device keeps the signed-in business's colour. public/quotemaker-bootstrap.js reads it before the app loads. */
export const TENANT_ACCENT_CACHE_KEY = 'qm_tenant_brand_accent';

type CachedAccent = { tenantId: string; hex: string };

function readCachedAccent(): CachedAccent | null {
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(TENANT_ACCENT_CACHE_KEY) : null;
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const hex = validHex(parsed?.hex);
        if (!hex || typeof parsed?.tenantId !== 'string' || !parsed.tenantId) return null;
        return { tenantId: parsed.tenantId, hex };
    } catch {
        return null;
    }
}

function writeCachedAccent(tenantId: string | null | undefined, hex: string | null): void {
    try {
        if (typeof localStorage === 'undefined') return;
        if (!tenantId || !hex) localStorage.removeItem(TENANT_ACCENT_CACHE_KEY);
        else localStorage.setItem(TENANT_ACCENT_CACHE_KEY, JSON.stringify({ tenantId, hex }));
    } catch {
        /* storage unavailable: the next boot waits for the profile instead */
    }
}

function brandKillSwitchOn(): boolean {
    return typeof window !== 'undefined'
        && Boolean((window as unknown as { qm_disable_brand_from_tenant?: boolean }).qm_disable_brand_from_tenant);
}

/** Remove an injected tenant accent so the tokens.css default applies again. */
export function clearBrandAccent(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement.style;
    for (const prop of ACCENT_PROPERTIES) root.removeProperty(prop);
    setBrowserThemeColor(DEFAULT_ACCENT);
}

/**
 * Apply the signed-in tenant's brand colour to the whole app. Called at boot
 * (app-boot-warmup) and by useBrand(). A tenant without a valid colour, or
 * with the default, gets tokens.css as written.
 */
export function applyTenantBrandAccent(primaryColor: string | null | undefined, tenantId?: string | null): void {
    if (brandKillSwitchOn()) return;
    const hex = validHex(primaryColor);
    if (hex && hex.toLowerCase() !== DEFAULT_ACCENT.toLowerCase()) {
        applyBrandAccent(hex);
        writeCachedAccent(tenantId, hex);
        return;
    }
    clearBrandAccent();
    writeCachedAccent(tenantId, null);
}

/**
 * Re-apply the colour this device last saw for the tenant, before the profile
 * request returns, so a reload does not flash the default brand first.
 * A colour cached for a DIFFERENT business (which the pre-app bootstrap may
 * already have painted) comes off at once. Returns the hex applied, or null.
 */
export function applyCachedTenantBrandAccent(tenantId: string | null | undefined): string | null {
    if (brandKillSwitchOn() || !tenantId) return null;
    const cached = readCachedAccent();
    if (!cached || cached.tenantId !== tenantId) {
        clearBrandAccent();
        return null;
    }
    applyBrandAccent(cached.hex);
    return cached.hex;
}

